import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, NonNullableFormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AppButtonComponent } from '../../../shared/components/button/app-button.component';
import { UiService } from '../../../core/services/ui.service';
import { UserService } from '../../../core/services/user.service';
import { EMAIL_REGEX } from '../../../core/constants/validation.constants';
import { SnackbarService } from '../../../core/services/snackbar.service';

type CreateUserForm = {
  email: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  isB2B: FormControl<boolean>;
  group: FormControl<string | null>;
  expirationDate: FormControl<string | null>;
};

type PickerEnabledInput = HTMLInputElement & { showPicker?: () => void };

@Component({
  selector: 'app-user-management-create-user-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AppButtonComponent],
  templateUrl: './user-management-create-user.component.html',
  styleUrls: ['./user-management-create-user.component.scss'],
})
export class CreateUserComponent implements OnDestroy {
  isVisible = false;
  isLoading = false;
  errorMessage = '';

  @ViewChild('expiryInput', { static: false }) expiryInput!: ElementRef<HTMLInputElement>;

  private subscription = new Subscription();

  createUserForm: FormGroup<CreateUserForm>;

  constructor(
    private uiService: UiService,
    private userManagementService: UserService,
    private fb: NonNullableFormBuilder,
    private snackbarService: SnackbarService,
  ) {
    const modalSub = this.uiService.createUserModal$.subscribe((visible) => {
      this.isVisible = visible;
    });

    this.subscription.add(modalSub);

    this.createUserForm = this.fb.group<CreateUserForm>({
      email: this.fb.control('', {
        validators: [Validators.required, Validators.pattern(EMAIL_REGEX)],
      }),
      firstName: this.fb.control('', {
        validators: [Validators.required],
      }),
      lastName: this.fb.control('', {
        validators: [Validators.required],
      }),
      isB2B: this.fb.control(false),
      group: this.fb.control({ value: '', disabled: true }),
      expirationDate: this.fb.control({ value: '', disabled: true }),
    });
    this.createUserForm.get('isB2B')!.valueChanges.subscribe((isB2B) => {
      const groupControl = this.createUserForm.get('group')!;
      const dateControl = this.createUserForm.get('expirationDate')!;
      if (isB2B) {
        groupControl.enable();
        dateControl.enable();
        const currentDate = new Date();
        const oneYearLater = new Date(currentDate.setFullYear(currentDate.getFullYear() + 1));
        const formatted = oneYearLater.toISOString().split('T')[0]; // yyyy-MM-dd

        if (!dateControl.value) {
          dateControl.setValue(formatted);
        }
      } else {
        groupControl.disable();
        dateControl.disable();
        dateControl.setValue(null);
      }
    });
  }

  closeModal(): void {
    this.uiService.closeCreateUserModal();
  }

  openDatePicker() {
    const input = this.expiryInput.nativeElement as PickerEnabledInput;
    if (typeof input.showPicker === 'function') {
      input.showPicker();
    } else {
      input.focus();
      input.click();
    }
  }

  onSubmit(): void {
    if (this.createUserForm.invalid) return;
    this.isLoading = true;
    this.errorMessage = '';

    const { firstName, lastName, email, isB2B, group, expirationDate } = this.createUserForm.getRawValue();

    this.userManagementService.createUserByAdmin(firstName, lastName, email, isB2B, group, expirationDate).subscribe({
      next: () => {
        this.isLoading = false;
        this.snackbarService.show('New user successfully added.', 'success');
        this.createUserForm.reset();
        this.closeModal();
      },
      error: (err) => {
        this.isLoading = false;
        this.errorMessage = err.error?.error || 'Error creating user';
      },
    });
  }

  get isFormInvalid(): boolean {
    return this.createUserForm.invalid;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
